import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Loader2, Upload, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';
import { getAiSessionId } from '@/utils/aiAttribution';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  'Carpet Tile',
  'Broadloom',
  'Luxury Vinyl Plank & Tile',
  'Commercial Mats',
  'Industrial & Anti-Fatigue',
  'Non-Slip Flooring',
  'Recycled Rubber Flooring',
  'Accessories',
  'Other'
];

const FastQuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    squareFootage: '',
    location: '',
    timeline: '',
    projectDetails: ''
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Keep multi-file uploads sane: cap count + per-file size + total size
  // to prevent one accidental 500MB drop from stalling the submit.
  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 25 * 1024 * 1024;   // 25 MB each
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 MB combined

  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('fast-quote', formData, setFormData, { enabled: !isSuccess });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    // Merge with any files already picked, dropping duplicates by (name,size,lastModified).
    const seen = new Set(files.map(f => `${f.name}|${f.size}|${f.lastModified}`));
    const next = [...files];
    for (const f of picked) {
      const key = `${f.name}|${f.size}|${f.lastModified}`;
      if (!seen.has(key)) {
        next.push(f);
        seen.add(key);
      }
    }

    if (next.length > MAX_FILES) {
      setErrors(prev => ({ ...prev, file: `Max ${MAX_FILES} files per submission` }));
      e.target.value = '';
      return;
    }
    for (const f of next) {
      if (f.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, file: `“${f.name}” is over 25 MB — please upload smaller` }));
        e.target.value = '';
        return;
      }
    }
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_SIZE) {
      setErrors(prev => ({ ...prev, file: `Combined attachments are over 100 MB — please remove one` }));
      e.target.value = '';
      return;
    }

    setFiles(next);
    setErrors(prev => ({ ...prev, file: undefined }));
    e.target.value = '';   // allow re-picking the same file after remove
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!formData.fullName.trim()) e.fullName = 'Full name is required';
    if (!formData.email.trim()) e.email = 'Work email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email address';
    if (!formData.company.trim()) e.company = 'Company name is required';
    if (!formData.projectType) e.projectType = 'Please select a project type';
    if (!formData.squareFootage.trim()) e.squareFootage = 'Estimated square footage is required';
    if (!formData.location.trim()) e.location = 'Project location is required';
    if (!formData.projectDetails.trim()) e.projectDetails = 'Project details are required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const body = new FormData();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      // Append each file under the same `files` field name — FastAPI
      // collects repeated field names into a List[UploadFile].
      files.forEach(f => body.append('files', f));
      const sid = getAiSessionId();
      if (sid) body.append('ai_session_id', sid);

      const res = await fetch(`/api/fast-quote`, { method: 'POST', body });
      if (!res.ok) throw new Error('Submit failed');
      clearDraft();
      setIsSuccess(true);
    } catch (err) {
      reportFailedSubmission({
        formType: 'fast_quote',
        draft: formData,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: 'Something went wrong. Please try again, or email sales@porticosystems.com directly.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setFormData({ fullName: '', email: '', company: '', phone: '', projectType: '', squareFootage: '', location: '', timeline: '', projectDetails: '' });
      setFiles([]);
      setErrors({});
      setIsSuccess(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && handleClose()}
        data-testid="fast-quote-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
          data-testid="fast-quote-modal"
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            data-testid="fast-quote-modal-close"
            aria-label="Close form"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>

          {!isSuccess ? (
            <form onSubmit={handleSubmit} className="p-6">
              <RestoreDraftBanner
                show={hasDraft}
                draftAge={draftAge}
                onRestore={restoreDraft}
                onDismiss={dismissDraftBanner}
                testId="fastquote-restore-draft"
              />
              <h2 className="font-heading text-2xl font-bold text-gray-900 mb-1 pr-10">Get a Fast Quote</h2>
              <p className="text-sm text-gray-500 mb-6">
                Tell us a few details about your project and we'll provide a quick, accurate estimate.
              </p>

              <div className="space-y-4">
                {/* Row: Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fq-name">Full Name *</Label>
                    <Input id="fq-name" value={formData.fullName} onChange={e => handleChange('fullName', e.target.value)} placeholder="John Smith" data-testid="fq-fullname" className={errors.fullName ? 'border-red-400' : ''} />
                    {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="fq-email">Work Email *</Label>
                    <Input id="fq-email" type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="john@company.com" data-testid="fq-email" className={errors.email ? 'border-red-400' : ''} />
                    {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                  </div>
                </div>

                {/* Row: Company + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fq-company">Company Name *</Label>
                    <Input id="fq-company" value={formData.company} onChange={e => handleChange('company', e.target.value)} placeholder="Acme Corp" data-testid="fq-company" className={errors.company ? 'border-red-400' : ''} />
                    {errors.company && <p className="text-xs text-red-500 mt-1">{errors.company}</p>}
                  </div>
                  <div>
                    <Label htmlFor="fq-phone">Phone Number</Label>
                    <Input id="fq-phone" type="tel" value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="(555) 123-4567" data-testid="fq-phone" />
                  </div>
                </div>

                {/* Project Type */}
                <div>
                  <Label>Project Type *</Label>
                  <Select value={formData.projectType} onValueChange={v => handleChange('projectType', v)}>
                    <SelectTrigger data-testid="fq-project-type" className={errors.projectType ? 'border-red-400' : ''}>
                      <SelectValue placeholder="Select a project type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.projectType && <p className="text-xs text-red-500 mt-1">{errors.projectType}</p>}
                </div>

                {/* Row: Sq Ft + Location */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="fq-sqft">Est. Square Footage *</Label>
                    <Input id="fq-sqft" value={formData.squareFootage} onChange={e => handleChange('squareFootage', e.target.value)} placeholder="e.g. 5,000" data-testid="fq-sqft" className={errors.squareFootage ? 'border-red-400' : ''} />
                    {errors.squareFootage && <p className="text-xs text-red-500 mt-1">{errors.squareFootage}</p>}
                  </div>
                  <div>
                    <Label htmlFor="fq-location">City & State *</Label>
                    <Input id="fq-location" value={formData.location} onChange={e => handleChange('location', e.target.value)} placeholder="Atlanta, GA" data-testid="fq-location" className={errors.location ? 'border-red-400' : ''} />
                    {errors.location && <p className="text-xs text-red-500 mt-1">{errors.location}</p>}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <Label htmlFor="fq-timeline">Project Timeline</Label>
                  <Input id="fq-timeline" value={formData.timeline} onChange={e => handleChange('timeline', e.target.value)} placeholder="e.g. Q3 2026, ASAP, 6 months" data-testid="fq-timeline" />
                </div>

                {/* Project Details */}
                <div>
                  <Label htmlFor="fq-details">Project Details *</Label>
                  <Textarea id="fq-details" value={formData.projectDetails} onChange={e => handleChange('projectDetails', e.target.value)} placeholder="Describe your project, requirements, or any specific products you're interested in..." rows={3} data-testid="fq-details" className={errors.projectDetails ? 'border-red-400' : ''} />
                  {errors.projectDetails && <p className="text-xs text-red-500 mt-1">{errors.projectDetails}</p>}
                </div>

                {/* File Upload */}
                <div>
                  <Label>Attach Files</Label>
                  <p className="text-xs text-gray-400 mb-2">
                    Plans, measurements, photos or reference files. Up to {MAX_FILES} files · 25 MB each · 100 MB total.
                  </p>
                  <label
                    htmlFor="fq-file"
                    className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
                    data-testid="fq-file-upload"
                  >
                    <Upload className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {files.length === 0 ? "Choose files or drag them here" : "Add more files"}
                    </span>
                    <input
                      id="fq-file"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.xlsx,.docx"
                    />
                  </label>
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1.5" data-testid="fq-file-list">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${f.size}-${f.lastModified}`}
                          className="flex items-center gap-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg"
                        >
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                            <p className="text-xs text-gray-400">
                              {f.size < 1024 * 1024
                                ? `${Math.round(f.size / 1024)} KB`
                                : `${(f.size / (1024 * 1024)).toFixed(1)} MB`}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            className="p-1 rounded hover:bg-gray-200 transition-colors"
                            data-testid={`fq-file-remove-${i}`}
                            aria-label={`Remove ${f.name}`}
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {errors.file && <p className="text-xs text-red-500 mt-1">{errors.file}</p>}
                </div>

                {errors.submit && (
                  <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg" data-testid="fq-error">{errors.submit}</p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-100">
                <Button type="submit" disabled={isSubmitting} className="w-full" data-testid="fq-submit-btn">
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
                <p className="text-xs text-gray-400 text-center mt-2">Response within 1 business day. No obligation.</p>
              </div>
            </form>
          ) : (
            <div className="p-6 text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">Quote Request Received!</h3>
              <p className="text-gray-600 mb-2">
                Thank you for your submission. Our team is reviewing your project details.
              </p>
              <p className="text-sm text-gray-400 mb-6">
                Response within 1 business day. No obligation.
              </p>
              <Button onClick={handleClose} className="w-full" data-testid="fq-done-btn">Done</Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FastQuoteModal;
